import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateRsvpOptions,
  BulkUpdateRsvpResponse,
  CheckInRsvpOptions,
  CheckInRsvpResponse,
  CreateRsvpOptions,
  CreateRsvpResponse,
  DeleteRsvpCheckInOptions,
  DeleteRsvpCheckInResponse,
  DeleteRsvpOptions,
  GetRsvpOptions,
  ListRsvpOptions,
  ListRsvpResponse,
  QueryRsvpOptions,
  QueryRsvpResponse,
  Rsvp,
  RsvpCreatedEnvelope,
  RsvpDeletedEnvelope,
  RsvpUpdatedEnvelope,
  UpdateRsvpOptions,
  UpdateRsvpResponse,
  bulkUpdateRsvp as universalBulkUpdateRsvp,
  checkInRsvp as universalCheckInRsvp,
  createRsvp as universalCreateRsvp,
  deleteRsvp as universalDeleteRsvp,
  deleteRsvpCheckIn as universalDeleteRsvpCheckIn,
  getRsvp as universalGetRsvp,
  listRsvp as universalListRsvp,
  queryRsvp as universalQueryRsvp,
  updateRsvp as universalUpdateRsvp,
} from './events-v1-rsvp-rsvp.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function listRsvp(httpClient: HttpClient): ListRsvpSignature {
  return (options?: ListRsvpOptions) =>
    universalListRsvp(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListRsvpSignature {
  /**
   * Retrieves a list of up to 100 RSVPs.
   * @param - Optional fields.
   * @deprecated
   */
  (options?: ListRsvpOptions): Promise<
    NonNullablePaths<
      ListRsvpResponse,
      | `total`
      | `offset`
      | `limit`
      | `rsvps`
      | `rsvps.${number}._id`
      | `rsvps.${number}.eventId`
      | `rsvps.${number}.contactId`
      | `rsvps.${number}.memberId`
      | `rsvps.${number}.firstName`
      | `rsvps.${number}.lastName`
      | `rsvps.${number}.email`
      | `rsvps.${number}.status`
      | `rsvps.${number}.totalGuests`
      | `rsvps.${number}.anonymized`,
      4
    >
  >;
}

export function queryRsvp(httpClient: HttpClient): QueryRsvpSignature {
  return (options?: QueryRsvpOptions) =>
    universalQueryRsvp(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryRsvpSignature {
  /**
   * Retrieves a list of up to 100 RSVPs.
   * @param - Optional fields.
   * @deprecated
   */
  (options?: QueryRsvpOptions): Promise<
    NonNullablePaths<
      QueryRsvpResponse,
      | `total`
      | `offset`
      | `limit`
      | `rsvps`
      | `rsvps.${number}._id`
      | `rsvps.${number}.eventId`
      | `rsvps.${number}.contactId`
      | `rsvps.${number}.memberId`
      | `rsvps.${number}.firstName`
      | `rsvps.${number}.lastName`
      | `rsvps.${number}.email`
      | `rsvps.${number}.status`
      | `rsvps.${number}.totalGuests`
      | `rsvps.${number}.anonymized`,
      4
    >
  >;
}

export function getRsvp(httpClient: HttpClient): GetRsvpSignature {
  return (rsvpId: string, options?: GetRsvpOptions) =>
    universalGetRsvp(
      rsvpId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRsvpSignature {
  /**
   * Retrieves an RSVP.
   * @param - RSVP ID.
   * @param - Optional fields.
   * @returns RSVP.
   * @deprecated
   */
  (rsvpId: string, options?: GetRsvpOptions): Promise<
    NonNullablePaths<
      Rsvp,
      | `_id`
      | `eventId`
      | `contactId`
      | `memberId`
      | `firstName`
      | `lastName`
      | `email`
      | `rsvpForm.inputValues`
      | `rsvpForm.inputValues.${number}.inputName`
      | `rsvpForm.inputValues.${number}.value`
      | `status`
      | `totalGuests`
      | `guests`
      | `guests.${number}.index`
      | `guests.${number}.fullName`
      | `guests.${number}._id`
      | `anonymized`,
      5
    >
  >;
}

export function createRsvp(httpClient: HttpClient): CreateRsvpSignature {
  return (options?: CreateRsvpOptions) =>
    universalCreateRsvp(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateRsvpSignature {
  /**
   * Creates an RSVP, associated with a contact of the site.
   * @param - Optional fields.
   * @deprecated
   */
  (options?: CreateRsvpOptions): Promise<
    NonNullablePaths<
      CreateRsvpResponse,
      | `rsvp._id`
      | `rsvp.eventId`
      | `rsvp.contactId`
      | `rsvp.memberId`
      | `rsvp.firstName`
      | `rsvp.lastName`
      | `rsvp.email`
      | `rsvp.rsvpForm.inputValues`
      | `rsvp.rsvpForm.inputValues.${number}.inputName`
      | `rsvp.rsvpForm.inputValues.${number}.value`
      | `rsvp.status`
      | `rsvp.totalGuests`
      | `rsvp.guests`
      | `rsvp.guests.${number}.index`
      | `rsvp.guests.${number}.fullName`
      | `rsvp.guests.${number}._id`
      | `rsvp.anonymized`
      | `calendarLinks.google`
      | `calendarLinks.ics`,
      6
    >
  >;
}

export function updateRsvp(httpClient: HttpClient): UpdateRsvpSignature {
  return (
    rsvpId: string,
    eventId: string,
    options?: NonNullablePaths<UpdateRsvpOptions, `fields`, 2>
  ) =>
    universalUpdateRsvp(
      rsvpId,
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateRsvpSignature {
  /**
   * Updates an RSVP.
   * @param - RSVP ID.
   * @param - Event ID to which RSVP belongs.
   * @param - Optional fields.
   * @deprecated
   */
  (
    rsvpId: string,
    eventId: string,
    options?: NonNullablePaths<UpdateRsvpOptions, `fields`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateRsvpResponse,
      | `rsvp._id`
      | `rsvp.eventId`
      | `rsvp.contactId`
      | `rsvp.memberId`
      | `rsvp.firstName`
      | `rsvp.lastName`
      | `rsvp.email`
      | `rsvp.rsvpForm.inputValues`
      | `rsvp.rsvpForm.inputValues.${number}.inputName`
      | `rsvp.rsvpForm.inputValues.${number}.value`
      | `rsvp.status`
      | `rsvp.totalGuests`
      | `rsvp.guests`
      | `rsvp.guests.${number}.index`
      | `rsvp.guests.${number}.fullName`
      | `rsvp.guests.${number}._id`
      | `rsvp.anonymized`,
      6
    >
  >;
}

export function bulkUpdateRsvp(
  httpClient: HttpClient
): BulkUpdateRsvpSignature {
  return (eventId: string, options?: BulkUpdateRsvpOptions) =>
    universalBulkUpdateRsvp(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateRsvpSignature {
  /**
   * Updates statuses of multiple RSVPs.
   * @param - Event ID to which RSVP belongs.
   * @param - Optional fields.
   * @deprecated
   */
  (eventId: string, options?: BulkUpdateRsvpOptions): Promise<
    NonNullablePaths<
      BulkUpdateRsvpResponse,
      | `rsvps`
      | `rsvps.${number}._id`
      | `rsvps.${number}.eventId`
      | `rsvps.${number}.contactId`
      | `rsvps.${number}.memberId`
      | `rsvps.${number}.firstName`
      | `rsvps.${number}.lastName`
      | `rsvps.${number}.email`
      | `rsvps.${number}.status`
      | `rsvps.${number}.totalGuests`
      | `rsvps.${number}.anonymized`,
      4
    >
  >;
}

export function deleteRsvp(httpClient: HttpClient): DeleteRsvpSignature {
  return (eventId: string, options?: DeleteRsvpOptions) =>
    universalDeleteRsvp(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteRsvpSignature {
  /**
   * Deletes an RSVP.
   * @param - Event ID to which RSVP belongs.
   * @param - Optional fields.
   * @deprecated
   */
  (eventId: string, options?: DeleteRsvpOptions): Promise<void>;
}

export function checkInRsvp(httpClient: HttpClient): CheckInRsvpSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<CheckInRsvpOptions, `rsvpId`, 2>
  ) =>
    universalCheckInRsvp(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CheckInRsvpSignature {
  /**
   * Checks-in an RSVP.
   * @param - Event ID to which RSVP belongs.
   * @param - Optional fields.
   * @deprecated
   */
  (
    eventId: string,
    options?: NonNullablePaths<CheckInRsvpOptions, `rsvpId`, 2>
  ): Promise<
    NonNullablePaths<
      CheckInRsvpResponse,
      | `rsvp._id`
      | `rsvp.eventId`
      | `rsvp.contactId`
      | `rsvp.memberId`
      | `rsvp.firstName`
      | `rsvp.lastName`
      | `rsvp.email`
      | `rsvp.rsvpForm.inputValues`
      | `rsvp.rsvpForm.inputValues.${number}.inputName`
      | `rsvp.rsvpForm.inputValues.${number}.value`
      | `rsvp.status`
      | `rsvp.totalGuests`
      | `rsvp.guests`
      | `rsvp.guests.${number}.index`
      | `rsvp.guests.${number}.fullName`
      | `rsvp.guests.${number}._id`
      | `rsvp.anonymized`,
      6
    >
  >;
}

export function deleteRsvpCheckIn(
  httpClient: HttpClient
): DeleteRsvpCheckInSignature {
  return (
    eventId: string,
    options?: NonNullablePaths<DeleteRsvpCheckInOptions, `rsvpId`, 2>
  ) =>
    universalDeleteRsvpCheckIn(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteRsvpCheckInSignature {
  /**
   * Deletes an RSVP check-in.
   * @param - Event ID to which RSVP belongs.
   * @param - Optional fields.
   * @deprecated
   */
  (
    eventId: string,
    options?: NonNullablePaths<DeleteRsvpCheckInOptions, `rsvpId`, 2>
  ): Promise<
    NonNullablePaths<
      DeleteRsvpCheckInResponse,
      | `rsvp._id`
      | `rsvp.eventId`
      | `rsvp.contactId`
      | `rsvp.memberId`
      | `rsvp.firstName`
      | `rsvp.lastName`
      | `rsvp.email`
      | `rsvp.rsvpForm.inputValues`
      | `rsvp.rsvpForm.inputValues.${number}.inputName`
      | `rsvp.rsvpForm.inputValues.${number}.value`
      | `rsvp.status`
      | `rsvp.totalGuests`
      | `rsvp.guests`
      | `rsvp.guests.${number}.index`
      | `rsvp.guests.${number}.fullName`
      | `rsvp.guests.${number}._id`
      | `rsvp.anonymized`,
      6
    >
  >;
}

export const onRsvpCreated = EventDefinition(
  'wix.events.rsvp.events.RsvpCreated',
  false,
  (event: RsvpCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.rsvpForm.inputValues.dateTime' },
            { path: 'data.guests.checkIn.created' },
            { path: 'data.guests.checkIn.checkInCanceledDate' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.rsvpForm.inputValues.number' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.rsvpForm.inputValues.address.address' }],
        },
      ])
    )
)<RsvpCreatedEnvelope>();
export const onRsvpDeleted = EventDefinition(
  'wix.events.rsvp.events.RsvpDeleted',
  false,
  (event: RsvpDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }],
        },
      ])
    )
)<RsvpDeletedEnvelope>();
export const onRsvpUpdated = EventDefinition(
  'wix.events.rsvp.events.RsvpUpdated',
  false,
  (event: RsvpUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.rsvpForm.inputValues.dateTime' },
            { path: 'data.guests.checkIn.created' },
            { path: 'data.guests.checkIn.checkInCanceledDate' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.rsvpForm.inputValues.number' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.rsvpForm.inputValues.address.address' }],
        },
      ])
    )
)<RsvpUpdatedEnvelope>();

export {
  AccountInfo,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BaseEventMetadata,
  BulkUpdateRsvpOptions,
  BulkUpdateRsvpRequest,
  BulkUpdateRsvpResponse,
  CalendarLinks,
  CheckIn,
  CheckInRsvpOptions,
  CheckInRsvpRequest,
  CheckInRsvpResponse,
  Counts,
  CreateRsvpOptions,
  CreateRsvpRequest,
  CreateRsvpResponse,
  DeleteRsvpCheckInOptions,
  DeleteRsvpCheckInRequest,
  DeleteRsvpCheckInResponse,
  DeleteRsvpOptions,
  DeleteRsvpRequest,
  DeleteRsvpResponse,
  FacetCounts,
  FormResponse,
  FormattedAddress,
  GetRsvpOptions,
  GetRsvpRequest,
  GetRsvpResponse,
  Guest,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  ListRsvpOptions,
  ListRsvpRequest,
  ListRsvpResponse,
  MessageEnvelope,
  ModificationOptions,
  OnlineConferencingLogin,
  QueryRsvpOptions,
  QueryRsvpRequest,
  QueryRsvpResponse,
  Rsvp,
  RsvpCreated,
  RsvpCreatedEnvelope,
  RsvpDeleted,
  RsvpDeletedEnvelope,
  RsvpFacetCounts,
  RsvpFacets,
  RsvpFieldset,
  RsvpStatus,
  RsvpTag,
  RsvpUpdated,
  RsvpUpdatedEnvelope,
  StandardDetails,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  UpdateRsvpOptions,
  UpdateRsvpRequest,
  UpdateRsvpResponse,
  WebhookIdentityType,
} from './events-v1-rsvp-rsvp.universal.js';
